import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api.js';
import type { BoardUpload, UploadedBoard } from '../api.js';

/**
 * Uploaded boards in the account panel: the player's own, and for an
 * admin every player's. `USER-BOARDS.md` at the top of the repository
 * has the design.
 *
 * The files are checked here before they are sent, for a quick answer;
 * the server checks all of it again. What happened, done or refused, is
 * said in a pop-up rather than on the panel.
 */

/** As the server: pixels a square in a board PNG, and the largest files. */
const PIXELS_PER_SQUARE = 75;
const MAX_BOARD_BYTES = 200 * 1024;
const MAX_PNG_BYTES = 3 * 1024 * 1024;

const message = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

/** The two files read and checked against each other, ready to send. */
async function readFiles(json: File, png: File): Promise<BoardUpload> {
  if (!/\.json$/i.test(json.name)) throw new Error('the board must be a .json file');
  if (!/\.png$/i.test(png.name)) throw new Error('the image must be a .png file');
  if (json.size > MAX_BOARD_BYTES) throw new Error(`the board.json is over ${MAX_BOARD_BYTES / 1024} KB`);
  if (png.size > MAX_PNG_BYTES) throw new Error(`the PNG is over ${MAX_PNG_BYTES / 1024 / 1024} MB`);

  const board = await json.text();
  let size: { width?: unknown; height?: unknown };
  try {
    size = JSON.parse(board) as typeof size;
  } catch {
    throw new Error('the board.json is not valid JSON');
  }
  if (typeof size.width !== 'number' || typeof size.height !== 'number') {
    throw new Error('the board.json has no width and height');
  }

  const bytes = new Uint8Array(await png.arrayBuffer());
  const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length < 24 || signature.some((b, i) => bytes[i] !== b)) throw new Error('the image is not a PNG');
  const view = new DataView(bytes.buffer);
  const w = view.getUint32(16);
  const h = view.getUint32(20);
  const wantW = size.width * PIXELS_PER_SQUARE;
  const wantH = size.height * PIXELS_PER_SQUARE;
  if (w !== wantW || h !== wantH) {
    throw new Error(`the PNG is ${w}x${h}; a ${size.width}x${size.height} board's is ${wantW}x${wantH}`);
  }

  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return { board, png: btoa(binary) };
}

/** A name and the two files, and a button that sends them. */
function BoardFiles({ label, nameRequired, onSend, onCancel }: {
  label: string;
  nameRequired: boolean;
  onSend: (upload: BoardUpload) => Promise<void>;
  onCancel?: () => void;
}) {
  const [name, setName] = useState('');
  const [json, setJson] = useState<File | null>(null);
  const [png, setPng] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(0);

  const send = async () => {
    if (!json || !png) return;
    setBusy(true);
    try {
      const upload = await readFiles(json, png);
      await onSend({ ...upload, ...(name.trim() ? { name: name.trim() } : {}) });
      setName('');
      setJson(null);
      setPng(null);
      setKey((k) => k + 1);
    } catch (err) {
      window.alert(err instanceof ApiError || err instanceof Error ? err.message : 'Could not send that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="board-files" key={key}>
      <label className="mono muted">
        Display Name
        <input
          placeholder={nameRequired ? '' : 'unchanged if left blank'}
          maxLength={64}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="mono muted">
        Board.json <input type="file" accept=".json,application/json" onChange={(e) => setJson(e.target.files?.[0] ?? null)} />
      </label>
      <label className="mono muted">
        Board.png <input type="file" accept=".png,image/png" onChange={(e) => setPng(e.target.files?.[0] ?? null)} />
      </label>
      <div className="row">
        <button
          className="secondary tiny"
          disabled={busy || !json || !png || (nameRequired && !name.trim())}
          onClick={() => void send()}
        >
          {busy ? 'Sending...' : label}
        </button>
        {onCancel && <button className="secondary tiny" onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}

/** Asks before a delete, naming the games that go with the board. */
function confirmDelete(board: UploadedBoard): boolean {
  const games = board.liveGames === 0 ? ''
    : ` ${board.liveGames} game${board.liveGames === 1 ? '' : 's'} in the lobby or in play on it will be deleted too.`;
  return window.confirm(`Delete ${board.name}?${games}`);
}

/** Both lists show the player's boards, so a change in either reloads
 * both: `version` moves on every change, and `onChange` moves it. */
interface Shared { version: number; onChange: () => void }

/** The player's own boards, and the upload. */
export function MyBoards({ version, onChange }: Shared) {
  const [boards, setBoards] = useState<UploadedBoard[] | null>(null);
  const [max, setMax] = useState(0);
  const [replacing, setReplacing] = useState<string | null>(null);

  const load = useCallback(() => {
    void api.myBoards().then((r) => { setBoards(r.boards); setMax(r.max); })
      .catch((err) => window.alert(message(err, 'Could not read your boards.')));
  }, []);
  useEffect(load, [load, version]);

  if (boards === null) return null;

  return (
    <div>
      <label>My boards ({boards.length} of {max})</label>
      <ul className="board-uploads">
        {boards.map((b) => (
          <li key={b.id}>
            <div className="spread">
              <a href={b.image} target="_blank" rel="noreferrer">{b.name}</a>
              <span className="row">
                <span className="mono muted">{b.width}x{b.height}</span>
                <button className="secondary tiny" onClick={() => setReplacing(replacing === b.id ? null : b.id)}>
                  Replace
                </button>
                <button
                  className="secondary tiny"
                  onClick={() => {
                    if (!confirmDelete(b)) return;
                    void api.deleteBoard(b.id).then(() => { window.alert(`Deleted ${b.name}.`); onChange(); })
                      .catch((err) => window.alert(message(err, 'Could not delete that.')));
                  }}
                >
                  Delete
                </button>
              </span>
            </div>
            {replacing === b.id && (
              <BoardFiles
                label="Replace"
                nameRequired={false}
                onSend={async (upload) => {
                  const r = await api.replaceBoard(b.id, upload);
                  setReplacing(null);
                  window.alert(`Replaced ${r.board.name}.`);
                  onChange();
                }}
                onCancel={() => setReplacing(null)}
              />
            )}
          </li>
        ))}
      </ul>
      {boards.length < max ? (
        <>
          <label style={{ marginTop: '0.6rem' }}>Upload a Board</label>
          <BoardFiles
            label="Upload"
            nameRequired
            onSend={async (upload) => {
              const r = await api.uploadBoard(upload);
              window.alert(`Uploaded ${r.board.name}.`);
              onChange();
            }}
          />
        </>
      ) : (
        <p className="mono muted">Delete a board to upload another.</p>
      )}
    </div>
  );
}

/** For an admin: every board that came from an upload. */
export function AdminBoards({ version, onChange }: Shared) {
  const [boards, setBoards] = useState<Awaited<ReturnType<typeof api.adminBoards>>['boards'] | null>(null);
  const [replacing, setReplacing] = useState<string | null>(null);

  const load = useCallback(() => {
    void api.adminBoards().then((r) => setBoards(r.boards))
      .catch((err) => window.alert(message(err, 'Could not read the uploaded boards.')));
  }, []);
  useEffect(load, [load, version]);

  if (boards === null) return null;

  return (
    <div>
      <label>Uploaded boards</label>
      {boards.length === 0 && <p className="mono muted">None.</p>}
      <ul className="board-uploads">
        {boards.map((b) => (
          <li key={b.id}>
            <div className="spread">
              <span>
                <a href={b.image} target="_blank" rel="noreferrer">{b.name}</a>
                <span className="mono muted">
                  {' '}{b.ownerId === null
                    ? `published${b.expansion ? `, ${b.expansion}` : ''}`
                    : `by ${b.owner ?? 'a deleted account'} (user ${b.ownerId})`}
                </span>
              </span>
              <span className="row">
                {b.ownerId !== null && (
                  <button
                    className="secondary tiny"
                    onClick={() => {
                      const name = window.prompt('Publish under the name', b.name);
                      if (name === null) return;
                      const expansion = window.prompt('In the expansion (blank for none)', '');
                      if (expansion === null) return;
                      void api.publishBoard(b.id, name, expansion)
                        .then(() => { window.alert(`Published ${name || b.name}.`); onChange(); })
                        .catch((err) => window.alert(message(err, 'Could not publish that.')));
                    }}
                  >
                    Publish
                  </button>
                )}
                <button className="secondary tiny" onClick={() => setReplacing(replacing === b.id ? null : b.id)}>
                  Replace
                </button>
                <button
                  className="secondary tiny"
                  onClick={() => {
                    if (!confirmDelete(b)) return;
                    void api.adminDeleteBoard(b.id).then(() => { window.alert(`Deleted ${b.name}.`); onChange(); })
                      .catch((err) => window.alert(message(err, 'Could not delete that.')));
                  }}
                >
                  Delete
                </button>
              </span>
            </div>
            {replacing === b.id && (
              <BoardFiles
                label="Replace"
                nameRequired={false}
                onSend={async (upload) => {
                  const r = await api.adminReplaceBoard(b.id, upload);
                  setReplacing(null);
                  window.alert(`Replaced ${r.board.name}.`);
                  onChange();
                }}
                onCancel={() => setReplacing(null)}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
